import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { BUSINESS_TOOL_SECTIONS } from 'constants/businessTool';
import { t } from 'i18next';
import { icons } from 'PDF/assets/icons';
import MissionIcon from 'PDF/assets/icons/MissionIcon.png';
import MissionIconBlack from 'PDF/assets/icons/MissionIconBlack.png';
import ValuesIcon from 'PDF/assets/icons/ValuesIcon.png';
import ValuesIconBlack from 'PDF/assets/icons/ValuesIconBlack.png';
import VisionIcon from 'PDF/assets/icons/VisionIcon.png';
import VisionIconBlack from 'PDF/assets/icons/VisionIconBlack.png';
import { IGoalsTableData, IGoalsTableDataItem } from 'PDF/components/GoalsTable/types';
import { IEvent } from 'PDF/components/Timeline/VerticalTimeline';
import { getColorType } from 'PDF/helpers/documentStyle';
import { formatDateForPDF } from 'PDF/helpers/formatDates';
import { formatInternationPhoneNumber } from 'PDF/helpers/fotmatPhonePDF';
import { hasDataToRender } from 'PDF/helpers/hasDataToRender';
import { getMagazineTextObject } from 'PDF/helpers/pdfMapping';
import {
  formatContactPersonData,
  formatFounderInfo,
  getPriorityForExamplePage,
} from 'PDF/pages/AboutTheCompany/helpers';
import { IAboutCompanyFormData, IMissionData } from 'PDF/pages/AboutTheCompany/types';

import { IBusinessPlanType } from 'interfaces/businessPlan';

import { IGlobalSection } from 'components/BusinessToolForm/Sections/Global/form';
import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

const getMissionAndVisionTableData = (
  global: IGlobalSection,
  aboutCompanyInfo: IAboutCompanyFormData,
  isExamplePage: boolean,
) => {
  const {
    missionAndStrategy: { coreValues, visionOfFuture },
  } = aboutCompanyInfo;
  const { mission } = global;
  const colorType = getColorType();
  const isBlueYellowType = colorType === 'blueYellow';

  const data = {
    mission: {
      type: isExamplePage ? 'Mission Statement' : t('pdfPages.mappings.aboutCompany.missionStatement'),
      iconSrc: isBlueYellowType ? MissionIconBlack : MissionIcon,
      iconTextColor: isBlueYellowType ? 'black' : 'white',
      content: mission,
    },
    visionOfFuture: {
      type: isExamplePage ? 'Vision Statement' : t('pdfPages.mappings.aboutCompany.visionStatement'),
      iconSrc: isBlueYellowType ? VisionIconBlack : VisionIcon,
      iconTextColor: isBlueYellowType ? 'black' : 'white',
      content: visionOfFuture,
    },
    coreValues: {
      type: isExamplePage ? 'Company Values' : t('pdfPages.mappings.aboutCompany.companyValues'),
      iconSrc: isBlueYellowType ? ValuesIconBlack : ValuesIcon,
      iconTextColor: isBlueYellowType ? 'black' : 'white',
      content: coreValues,
    },
  };

  const missionData: IMissionData[] = (Object.keys(data) as Array<keyof typeof data>)
    .filter((key) => data[key].content)
    .map((key) => {
      return {
        type: data[key].type,
        data: data[key].content,
        iconSrc: data[key].iconSrc,
        iconTextColor: data[key].iconTextColor,
      };
    });

  return missionData;
};

const getGoalsAndObjectivesTableData = (
  aboutCompanyInfo: IAboutCompanyFormData,
  isExamplePage: boolean,
): IGoalsTableData => {
  const {
    missionAndStrategy: { goals },
  } = aboutCompanyInfo;

  const mappedData = goals.map((item) => ({
    ...item,
    priority: item?.priority,
  }));

  const goalsSplitByTerm = mappedData.reduce(
    (acc: { longTerm: IGoalsTableDataItem; shortTerm: IGoalsTableDataItem }, item) => {
      const { terms, goal, priority, scheduledDate, mechanismOfAchievement } = item;

      const contentItem = [
        goal,
        typeof priority === 'string'
          ? isExamplePage
            ? getPriorityForExamplePage(t(`levels.${priority.toLowerCase()}`))
            : t(`levels.${priority.toLowerCase()}`)
          : isExamplePage
            ? getPriorityForExamplePage(t(`levels.${priority?.value?.toLowerCase()}`))
            : t(`levels.${priority?.value?.toLowerCase()}`) || '',
        formatDateForPDF(scheduledDate, 'MMMM YYYY', isExamplePage),
        mechanismOfAchievement,
      ];

      if (contentItem.every((item) => item === '')) {
        return acc;
      }

      if (terms?.value === 'shortTerm') {
        acc.shortTerm.content.push(contentItem);
      } else {
        acc.longTerm.content.push(contentItem);
      }

      return acc;
    },
    {
      longTerm: { label: isExamplePage ? 'Long-term' : t('pdfPages.mappings.aboutCompany.longTerm'), content: [] },
      shortTerm: { label: isExamplePage ? 'Short-term' : t('pdfPages.mappings.aboutCompany.shortTerm'), content: [] },
    },
  );

  return {
    header: [
      {
        label: isExamplePage ? 'Goal' : t('pdfPages.mappings.aboutCompany.goal'),
        width: '149px',
        isBold: true,
      },
      {
        label: isExamplePage ? 'Priority' : t('pdfPages.mappings.aboutCompany.priority'),
        width: '53px',
        isCenter: true,
      },
      {
        label: isExamplePage ? 'Scheduled date' : t('pdfPages.mappings.aboutCompany.scheduledDate'),
        width: '104px',
        isBold: true,
        isCenter: true,
      },
      {
        label: isExamplePage ? 'Mechanism of achievement' : t('pdfPages.mappings.aboutCompany.mechanismOfAchievement'),
        width: '173px',
      },
    ],
    data: goalsSplitByTerm,
  };
};

const getGeneralCompanyInfoTableData = (
  global: IGlobalSection,
  aboutCompanyInfo: IAboutCompanyFormData,
  isExamplePage: boolean,
) => {
  const aboutCompanyGeneralInfo = aboutCompanyInfo.generalInfo;

  const {
    legalStructure,
    contactPersonPhone,
    contactPersonPhoneFormat,
    contactPersonsEmail,
    email,
    phone,
    phoneFormat,
    other,
    instagram,
    facebook,
    linkedin,
    firstName,
    lastName,
    position,
    websiteUrl,
    listOfFounders,
  } = aboutCompanyGeneralInfo;

  const { companyName, companyIndustry, numberOfEmployees, address, registrationDate } = global;
  const lastNameStr = lastName ? ` ${lastName}` : '';

  const {
    AddressOfRegistrationIcon,
    ContactPersonIcon,
    EmailIcon,
    FacebookIcon,
    FoundersListIcon,
    OperatingIndustryIcon,
    OtherSocialNetworksIcon,
    InstagramIcon,
    LegalStructureIcon,
    LinkedinIcon,
    NameIcon,
    NumberOfEmployeesIcon,
    PhoneIcon,
    RegistrationDateIcon,
    SocialNetworksIcon,
    WebsiteIcon,
  } = icons[getColorType()];

  const contactPerson = formatContactPersonData([
    `${firstName}${lastNameStr}`,
    position,
    contactPersonsEmail,
    contactPersonPhone ? formatInternationPhoneNumber(contactPersonPhone, contactPersonPhoneFormat) : '',
  ]);

  const networks = [
    { iconSrc: InstagramIcon, link: instagram },
    { iconSrc: FacebookIcon, link: facebook },
    { iconSrc: LinkedinIcon, link: linkedin },
    { iconSrc: OtherSocialNetworksIcon, link: other },
  ].filter(({ link }) => link);

  return [
    {
      id: 'name',
      title: isExamplePage ? 'Name' : t('pdfPages.mappings.aboutCompany.name'),
      iconSrc: NameIcon,
      info: companyName,
    },
    {
      id: 'legalStructure',
      title: isExamplePage ? 'Legal Structure' : t('pdfPages.mappings.aboutCompany.legalStructure'),
      iconSrc: LegalStructureIcon,
      info: legalStructure,
    },
    // TODO change type for industry inside form (string => label, value)
    // @ts-ignore
    {
      id: 'companyIndustry',
      title: isExamplePage ? 'Operating Industry' : t('pdfPages.mappings.aboutCompany.operatingIndustry'),
      iconSrc: OperatingIndustryIcon,
      // @ts-ignore
      info: companyIndustry?.label,
    },
    {
      id: 'numberOfEmployees',
      title: isExamplePage ? 'Number of Employees' : t('pdfPages.mappings.aboutCompany.numberOfEmployees'),
      iconSrc: NumberOfEmployeesIcon,
      info: numberOfEmployees,
    },
    {
      id: 'address',
      title: isExamplePage ? 'Business Location' : t('pdfPages.mappings.aboutCompany.address'),
      iconSrc: AddressOfRegistrationIcon,
      info: address,
    },
    {
      id: 'registrationDate',
      title: isExamplePage ? 'Registration Date' : t('pdfPages.mappings.aboutCompany.registrationDate'),
      iconSrc: RegistrationDateIcon,
      info: formatDateForPDF(registrationDate, 'MM.DD.YYYY', false),
    },
    {
      id: 'email',
      title: isExamplePage ? 'E-Mail' : t('pdfPages.mappings.aboutCompany.email'),
      iconSrc: EmailIcon,
      info: email || '',
    },
    {
      id: 'phone',
      title: isExamplePage ? 'Phone' : t('pdfPages.mappings.aboutCompany.phone'),
      iconSrc: PhoneIcon,
      info: phone ? formatInternationPhoneNumber(phone, phoneFormat) : '',
    },
    {
      id: 'websiteUrl',
      title: isExamplePage ? 'Website' : t('pdfPages.mappings.aboutCompany.websiteUrl'),
      iconSrc: WebsiteIcon,
      info: websiteUrl || '',
    },
    {
      id: 'contactPerson',
      title: isExamplePage ? 'Contact Person' : t('pdfPages.mappings.aboutCompany.contactPerson'),
      iconSrc: ContactPersonIcon,
      info: contactPerson,
    },
    {
      id: 'socialNetworks',
      title: isExamplePage ? 'Social Networks' : t('pdfPages.mappings.aboutCompany.socialNetworks'),
      iconSrc: SocialNetworksIcon,
      info: networks.length ? networks : '',
    },
    {
      id: 'listOfFounders',
      title: isExamplePage ? 'Founders List' : t('pdfPages.mappings.aboutCompany.listOfFounders'),
      iconSrc: FoundersListIcon,
      info: hasDataToRender(listOfFounders) ? formatFounderInfo(listOfFounders, isExamplePage) : '',
    },
  ];
};

const getAboutCompanyTextData = (
  global: IGlobalSection,
  aboutCompanyInfo: IAboutCompanyFormData,
  businessPlanType: IBusinessPlanType,
  isExamplePage: boolean,
) => {
  const { companyBriefDescription, projectBriefDescription } = global;
  const {
    companyHistory: { businessIdeaConception, motivation },
    missionAndStrategy: { problemOfMarket, howProjectAddressesTheProblem },
  } = aboutCompanyInfo;

  return [
    [
      getMagazineTextObject(
        isExamplePage ? 'Company Brief. ' : t('pdfPages.mappings.aboutCompany.companyBrief'),
        companyBriefDescription,
      ),
    ],
    businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS
      ? [
          getMagazineTextObject(
            isExamplePage ? 'Description of the Project. ' : t('pdfPages.mappings.aboutCompany.projectDescr'),
            projectBriefDescription,
          ),
        ]
      : [
          getMagazineTextObject(
            isExamplePage ? 'Business Idea Origin. ' : t('pdfPages.mappings.aboutCompany.businessIdeaOrigin'),
            businessIdeaConception,
          ),
          getMagazineTextObject(
            isExamplePage ? 'Creation Reason. ' : t('pdfPages.mappings.aboutCompany.creationReason'),
            motivation,
          ),
        ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Problem Definition. ' : t('pdfPages.mappings.aboutCompany.problemDef'),
        problemOfMarket,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Contribution to Problem Solving. ' : t('pdfPages.mappings.aboutCompany.contributionToProblem'),
        howProjectAddressesTheProblem,
      ),
    ],
  ];
};

const getSwotTableData = (global: IGlobalSection, aboutCompanyInfo: IAboutCompanyFormData) => {
  const aboutCompanySwotInfo = aboutCompanyInfo.swot;
  const { companyStrengths, companyWeaknesses } = global;

  return {
    ...aboutCompanySwotInfo,
    companyStrengths,
    companyWeaknesses,
  };
};

const getHistoricalEventsTimelineData = (aboutCompanyInfo: IAboutCompanyFormData): IEvent[] => {
  const data = aboutCompanyInfo.companyHistory.events;
  const sortedByDate = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return sortedByDate
    .filter(({ title, date }) => title && date)
    .map(({ title, date }) => ({
      step: formatDateForPDF(date, 'YYYY', false),
      title,
    }));
};

export const getAboutCompanyProps = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessPlanType,
  isExamplePage: boolean,
) => {
  const global: IGlobalSection = businessPlanChapters[BUSINESS_TOOL_SECTIONS.GLOBAL];
  const aboutCompanyInfo = businessPlanChapters[BUSINESS_TOOL_SECTIONS.ABOUT_COMPANY];

  return {
    magazineItems: getAboutCompanyTextData(global, aboutCompanyInfo, businessPlanType, isExamplePage),
    generalInfoTableData: getGeneralCompanyInfoTableData(global, aboutCompanyInfo, isExamplePage),
    missionAndVisionTableData: getMissionAndVisionTableData(global, aboutCompanyInfo, isExamplePage),
    goalsAndObjectivesTableData: getGoalsAndObjectivesTableData(aboutCompanyInfo, isExamplePage),
    swotTableData: getSwotTableData(global, aboutCompanyInfo),
    historicalEventsTimelineData: getHistoricalEventsTimelineData(aboutCompanyInfo),
  };
};
